import { useEffect, useState, useCallback, useRef } from 'react'
import { supabase } from './lib/supabase'
import { loadStaffIdentity, loadBranchData, loadMyVerifiedCounts } from './lib/data'
import Login from './pages/Login'
import SalesEntry from './pages/SalesEntry'
import Stock from './pages/Stock'
import Store from './pages/Store'
import Corrections from './pages/Corrections'
import DailySales from './pages/DailySales'
import RoomBoard from './pages/RoomBoard'
import StaySettings from './pages/StaySettings'
import { audioReady, pulseAlert, setAppBadge, unlockAudio } from './lib/alert'
import Catalog from './pages/Catalog'
import Payroll from './pages/Payroll'
import StaffAccounts from './pages/StaffAccounts'
import Variances from './pages/Variances'
import Review from './pages/Review'
import MyWeek from './pages/MyWeek'
import TillChecks from './pages/TillChecks'
import MyPay from './pages/MyPay'
import Recovery from './pages/Recovery'
import More from './pages/More'
import { ToastHost } from './components/Toast'
import { registerHandlers, flush } from './lib/outbox'
import { saveBasket, saveWriteoff, saveMovements, loadBranches,
         saveRepayment, saveCountLine, loadPendingVerifications,
         loadUnfinishedCounts, loadMyPay } from './lib/data'
import Credit from './pages/Credit'
import Counts from './pages/Counts'
import Shell from './components/Shell'
import ErrorBoundary from './components/ErrorBoundary'
import InstallHint from './components/InstallHint'
import { OVERSIGHT, is } from './lib/roles'
import { signOutCleanly } from './lib/push'
import { landingTabFor } from './lib/tabs'
import { logActivity, PAGE_NAMES } from './lib/activity'
import Activity from './pages/Activity'
import Supplies from './pages/Supplies'
import Attendance from './pages/Attendance'
import { startPresence } from './lib/presence'
import { tracked } from './lib/loadfail'

export default function App() {
  const [session, setSession] = useState(undefined) // undefined = loading
  const [identity, setIdentity] = useState(undefined) // undefined = not loaded yet, null = no session, false = session but no staff row
  const [boot, setBoot] = useState(null)
  const [tab, setTab] = useState('sales')
  const [err, setErr] = useState(null)
  const [branches, setBranches] = useState([])
  const [viewBranch, setViewBranch] = useState(null)
  const [pendingCount, setPendingCount] = useState(0)
  const [pendingLoaded, setPendingLoaded] = useState(false)
  const [myPay, setMyPay] = useState(null)
  const prevPending = useRef(null)
  // Declared here, not beside their effect below: the badge effect's
  // dependency array references justVerified and dep arrays ARE
  // evaluated during render, which would be a temporal dead zone.
  const lastVerifiedSeen = useRef(null)
  const [justVerified, setJustVerified] = useState(0)
  const [unfinished, setUnfinished] = useState([])

  const ALERT_ROLES = OVERSIGHT
  // Guards the role-based default-tab effect below so it applies
  // only once per real sign-in, not on every identity object
  // refresh (e.g. an auth token refresh) — declared here, before any
  // effect references it.
  const landedRef = useRef(false)

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session))
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s))
    return () => sub.subscription.unsubscribe()
  }, [])

  // identity (auth check + staff row) only needs to run once per
  // session — not once per branch switch
  useEffect(() => {
    if (!session) { setIdentity(undefined); setBoot(null); setViewBranch(null); setMyPay(null); landedRef.current = false; return }
    loadStaffIdentity().then(staff => setIdentity(staff === undefined ? null : (staff || false)))
      .catch(e => setErr(e.message))
  }, [session])

  // Lands each role on ITS OWN first tab — hardcoding a single
  // default (previously always 'sales') meant oversight roles landed
  // on a tab that isn't even one of their own direct tabs anymore,
  // showing "More" as the highlighted nav item on sign-in instead of
  // their actual primary screen. Guarded to apply once, on the first
  // real sign-in only — identity gets a new object reference on every
  // auth token refresh too, and without the guard this would yank
  // someone back to their default tab mid-session every time that
  // happens, not just on an actual fresh sign-in.
  useEffect(() => {
    if (!identity || landedRef.current) return
    landedRef.current = true
    // A notification that opens the app should open the PAGE, not the
    // front door (Chris, 25 October 2026). Tapping "2 stock counts need
    // verifying" used to land on the role's usual first tab, leaving the
    // person to find the counts themselves — which is most of the reason
    // to send the notification at all.
    //
    // Allow-listed, not read straight through: a link is something
    // anyone can craft, and the pages here are the ones a notification
    // is ever sent about. Role-gated too, so the link cannot put a
    // barman on a page their role does not carry — the page's own data
    // is protected by RLS regardless, this is about not showing someone
    // an empty screen they cannot use.
    const asked = new URLSearchParams(window.location.search).get('tab')
    const allowed = asked === 'count' && is(identity.role, OVERSIGHT) ? 'count' : null
    if (asked) {
      // Cleared either way, so a later refresh does not keep dragging
      // them back to the same page.
      window.history.replaceState({}, '', window.location.pathname)
    }
    // Every other role lands on its FIRST tab (lib/tabs.js), so the
    // landing screen can never drift from the bar's order.
    setTab(allowed || landingTabFor(identity.role))
  }, [identity])

  // Housekeeping / Laundry have only Supplies and My pay — no More, no
  // Stock. If an old session was left on a page they no longer have, send
  // them back to Supplies rather than showing an empty screen.
  useEffect(() => {
    if (boot?.staff?.role !== 'housekeeper') return
    if (!['supplies', 'mypay'].includes(tab)) setTab('supplies')
  }, [boot?.staff?.role, tab])

  // Page visits for the GM's Staff activity screen. One entry per page opened;
  // staying on a page is not re-logged, but coming back to the app after a
  // long absence (30+ minutes in the background) logs the page again, so each
  // return to work counts as a visit.
  const lastPage = useRef({ tab: null, at: 0 })
  useEffect(() => {
    if (!identity?.id || !PAGE_NAMES[tab]) return
    // Right after sign-in the tab is still the placeholder; the landing effect swaps it next render.
    if (lastPage.current.tab === null && tab === 'sales' && landingTabFor(identity.role) !== 'sales') return
    if (lastPage.current.tab === tab && Date.now() - lastPage.current.at < 30 * 60000) return
    lastPage.current = { tab, at: Date.now() }
    logActivity('page', PAGE_NAMES[tab])
  }, [tab, identity?.id])
  useEffect(() => {
    if (!identity?.id) return
    const back = () => {
      if (document.visibilityState !== 'visible') return
      if (Date.now() - lastPage.current.at >= 30 * 60000 && PAGE_NAMES[tabRef.current]) {
        lastPage.current = { tab: tabRef.current, at: Date.now() }
        logActivity('page', PAGE_NAMES[tabRef.current])
      }
    }
    document.addEventListener('visibilitychange', back)
    return () => document.removeEventListener('visibilitychange', back)
  }, [identity?.id])
  const tabRef = useRef(tab)
  tabRef.current = tab

  // Attendance by location (9am-8pm, not GM/admin): see lib/presence.js.
  useEffect(() => {
    if (!identity?.id) return
    return startPresence(identity)
  }, [identity?.id, identity?.role])

  const refresh = useCallback(() => {
    if (identity === undefined) return          // still loading identity
    if (identity === null) { setBoot(null); return }  // no session
    if (identity === false) { setBoot({ staff: null }); return } // not linked
    loadBranchData(identity, viewBranch).then(b => {
      setBoot(b)
      if (b?.seesAllBranches && !branches.length) loadBranches().then(setBranches)
    }).catch(e => setErr(e.message))
  }, [identity, viewBranch])

  useEffect(refresh, [refresh])

  // Own pay slips: only people whose login GM/admin linked to a payroll employee.
  useEffect(() => {
    if (!boot?.staff?.id) { setMyPay(null); return }
    tracked('My pay', loadMyPay()).then(setMyPay).catch(() => setMyPay(null))
  }, [boot?.staff?.id])

  // badge for auditor/storekeeper/manager/gm/admin: how many counts
  // are waiting for verification right now. Refetches on branch
  // switch and every 60s while the app stays open, so it stays
  // current without needing a full reload — this is an in-app alert,
  // not a push notification, so it only updates while someone is
  // actually looking at the app.
  useEffect(() => {
    if (!boot?.staff || !ALERT_ROLES.includes(boot.staff.role)) {
      setPendingCount(0); setPendingLoaded(false); prevPending.current = null; return
    }
    const check = () => loadPendingVerifications(boot.staff.branch_id)
      .then(n => { setPendingCount(n); setPendingLoaded(true) })
    check()
    const id = setInterval(check, 60000)
    return () => clearInterval(id)
  }, [boot?.staff?.branch_id, boot?.staff?.role])

  // Tone + icon badge follow pendingCount. The tone fires only when
  // the count RISES — re-alerting on every 60s poll for something
  // already seen would train people to ignore it. prevPending starts
  // at null so the first poll after sign-in never sounds for a
  // backlog that was already there before the app opened.
  // pendingLoaded matters: pendingCount starts at 0, so without it the
  // FIRST poll returning an existing backlog looks like a rise from
  // zero and sounds on load — before any user gesture, which Chrome
  // blocks and logs as an AudioContext error. The first real value
  // only seeds the baseline; the alert starts from the second.
  useEffect(() => {
    if (!pendingLoaded) return
    setAppBadge(pendingCount + justVerified)
    if (prevPending.current !== null && pendingCount > prevPending.current) pulseAlert()
    prevPending.current = pendingCount
  }, [pendingCount, pendingLoaded, justVerified])

  // "Your count was verified" — for the person who DID the count, so
  // it runs for every role, unlike ALERT_ROLES which is about work
  // waiting for a verifier. Tracks the newest verified_at rather than
  // a count: verifying one while an older one leaves the 20-row window
  // keeps the total identical, so a count would miss it.
  useEffect(() => {
    if (!boot?.staff) { lastVerifiedSeen.current = null; setJustVerified(0); return }
    let cancelled = false
    const check = async () => {
      const { newest } = await loadMyVerifiedCounts(boot.staff.id, lastVerifiedSeen.current)
      if (cancelled || !newest) return
      // First pass only records where we are — otherwise signing in
      // would announce every past verification, the same mistake the
      // pending-count alert made.
      if (lastVerifiedSeen.current !== null) {
        pulseAlert()
        setJustVerified(n => n + 1)
      }
      lastVerifiedSeen.current = newest
    }
    // Seed immediately, then poll on the same cadence as the other alert.
    loadMyVerifiedCounts(boot.staff.id, null).then(({ newest }) => {
      if (!cancelled) lastVerifiedSeen.current = newest || new Date().toISOString()
    })
    const id = setInterval(check, 60000)
    return () => { cancelled = true; clearInterval(id) }
  }, [boot?.staff?.id])

  // Browsers refuse to play audio until the user has interacted with the
  // page. Listeners STAY for the life of the app and only act when the
  // audio context is not running. They used to detach after the first
  // successful unlock — but on iPhone the context is suspended again
  // whenever the app is backgrounded, and with nothing left listening
  // there was nothing to wake it: the chime stayed silent until the app
  // was restarted. Cheap, since each call is a no-op once it is running.
  useEffect(() => {
    const wake = () => { if (!audioReady()) unlockAudio() }
    const onShow = () => { if (document.visibilityState === 'visible') wake() }
    window.addEventListener('pointerdown', wake)
    window.addEventListener('touchend', wake)
    document.addEventListener('visibilitychange', onShow)
    return () => {
      window.removeEventListener('pointerdown', wake)
      window.removeEventListener('touchend', wake)
      document.removeEventListener('visibilitychange', onShow)
    }
  }, [])

  // Unfinished (draft) stock counts — the "incomplete task" nudge.
  // Runs for anyone who can start a count (RLS then scopes what each
  // person actually sees: their own drafts, or the branch's for a
  // manager). Refetched on the same 60s cadence as the badge.
  useEffect(() => {
    if (!boot?.staff) { setUnfinished([]); return }
    const CAN_COUNT = ['bar', 'front_desk', 'storekeeper', 'manager', 'gm', 'admin', 'auditor']
    if (!CAN_COUNT.includes(boot.staff.role)) { setUnfinished([]); return }
    const check = () => loadUnfinishedCounts(boot.staff.branch_id).then(setUnfinished).catch(() => {})
    check()
    const id = setInterval(check, 60000)
    return () => clearInterval(id)
  }, [boot?.staff?.branch_id, boot?.staff?.role])

  // queued writes replay with the same code path as live ones
  useEffect(() => {
    registerHandlers({
      basket: (p) => saveBasket({
        staff: p.staffLite, locationId: p.locationId, date: p.date,
        customerId: p.customerId, payments: p.payments, lines: p.lines,
        backdateReason: p.backdateReason, onBehalfOf: p.onBehalfOf,
      }),
      movements: (p) => saveMovements(p.rows),
      repayment: (p) => saveRepayment({ ...p, staff: p.staffLite }),
      countLine: (p) => saveCountLine(p.countId, p.itemId, p.qty),
      writeoff: (p) => saveWriteoff({
        staff: p.staffLite, item: { id: p.itemId }, locationId: p.locationId,
        kind: p.kind, qty: p.qty, unitValue: p.unitValue, date: p.date,
        note: p.note || null, damageReason: p.damageReason || null,
      }),
    })
    flush()
  }, [])

  if (session === undefined) return <Center>Loading…</Center>
  if (!session) return <Login />
  if (err) return <Center>Couldn't load your profile. {err}</Center>
  if (!boot) return <Center>Loading…</Center>
  if (!boot.staff) return (
    <Center>
      <p className="max-w-xs text-center leading-relaxed">
        You're signed in, but this account isn't linked to a staff record yet.
        Ask a manager to link it, then reload.
      </p>
      <button onClick={() => window.confirm('Sign out?') && signOutCleanly()}
        className="mt-6 text-amber">Sign out</button>
    </Center>
  )

  return (
    <ErrorBoundary>
    <ToastHost>
    <Shell staff={boot.staff} tab={tab} onTab={setTab}
      branches={boot.seesAllBranches ? branches : []}
      viewBranch={boot.viewBranchId} onBranch={setViewBranch}
      pendingCount={pendingCount}
      alertEligible
      recordsSales={(boot.locations || []).some(l => l.is_sales_point && !l.is_store)}>
      {unfinished.length > 0 && tab !== 'count' && (
        <button onClick={() => setTab('count')}
          className="mx-5 mt-3 w-[calc(100%-2.5rem)] text-left rounded-xl border border-amber bg-amber/10 px-4 py-3">
          <div className="font-semibold text-amber">
            {unfinished.length === 1 ? 'A stock count was started but not finished'
              : `${unfinished.length} stock counts started but not finished`}
          </div>
          <div className="text-dim text-sm mt-0.5">
            {unfinished.length === 1 && unfinished[0].counter?.full_name
              ? `Started by ${unfinished[0].counter.full_name} · tap to finish or discard`
              : 'Tap to finish or discard'}
          </div>
        </button>
      )}
      {tab === 'sales' ? <SalesEntry boot={boot} />
        : tab === 'store' ? <Store boot={boot} />
        : tab === 'more' ? <More boot={boot} onGo={setTab} pendingCount={pendingCount} />
        : tab === 'dailysales' ? <DailySales boot={boot} />
        : tab === 'roomboard' ? <RoomBoard boot={boot} />
        : tab === 'staysettings' ? <StaySettings boot={boot} />
        : tab === 'catalog' ? <Catalog boot={boot} onChanged={refresh} />
        : tab === 'payroll' && !boot.staff.is_read_only ? <Payroll boot={boot} />
        : tab === 'staffaccounts' ? <StaffAccounts boot={boot} />
        : tab === 'activity' ? <Activity boot={boot} />
        : tab === 'attendance' ? <Attendance boot={boot} />
        : tab === 'mypay' && !boot.staff.is_read_only ? <MyPay data={myPay} boot={boot} />
        : tab === 'tillchecks' ? <TillChecks boot={boot} />
        : tab === 'variance' ? <Variances boot={boot} />
        : tab === 'review' ? <Review boot={boot} />
        : tab === 'myweek' ? <MyWeek boot={boot} />
        : tab === 'recovery' ? <Recovery boot={boot} />
        : tab === 'credit' ? <Credit boot={boot} />
        : tab === 'count' ? <Counts boot={boot} />
        : tab === 'fix' ? <Corrections boot={boot} />
        : tab === 'supplies' ? <Supplies boot={boot} />
        : <Stock boot={boot} />}
    </Shell>
    <InstallHint />
    </ToastHost>
    </ErrorBoundary>
  )
}

function Center({ children }) {
  return <div className="min-h-dvh flex flex-col items-center justify-center text-dim">{children}</div>
}
